import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth as useClerkAuth, useUser as useClerkUser } from "@clerk/clerk-react";
import type { AuthUser } from "../types";
import { getMe } from "../services/api";
import { identifyUser } from "../services/analytics";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded: authLoaded, getToken } = useClerkAuth();
  const { isLoaded: userLoaded } = useClerkUser();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!isSignedIn) {
      setUser(null);
      return;
    }
    try {
      const userData = await getMe();
      setUser(userData);
      identifyUser(userData.email, {
        is_admin: userData.is_admin,
        token_limit: userData.token_limit,
        tokens_used: userData.tokens_used,
      });
    } catch {
      setUser(null);
    }
  }, [isSignedIn]);

  // Store getToken for the axios interceptor
  useEffect(() => {
    if (getToken) {
      (window as any).__clerkGetToken = getToken;
    }
  }, [getToken]);

  // Fetch our user record once Clerk auth is ready
  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    if (isSignedIn) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [authLoaded, userLoaded, isSignedIn, refreshUser]);

  // Refresh token count after expensive operations
  useEffect(() => {
    const handler = () => refreshUser();
    window.addEventListener("tokens-changed", handler);
    return () => window.removeEventListener("tokens-changed", handler);
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: user?.is_admin ?? false,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
